import { Alert, Form, Select, Space, Typography } from 'antd'
import { GeckoMeasurementSystem } from 'types'

import { MethodAssignmentCard } from './MethodAssignmentCard'
import {
  resolveMethodAssignment,
  selectMethodAssignments,
} from './methodAssignments'
import { InspectionArea } from './planningTypes'
import {
  MethodAssignment,
  MethodPlanning,
  TechniqueCatalogue,
} from './techniqueTypes'

export const InspectionMethodsEditor = ({
  area,
  catalogue,
  config,
  measurementSystem,
  sourceRequiredArea,
  disabled,
  onChange,
}: {
  area: InspectionArea
  catalogue: TechniqueCatalogue
  config: MethodPlanning
  measurementSystem: GeckoMeasurementSystem
  sourceRequiredArea: number | undefined
  disabled: boolean
  onChange: (update: Partial<InspectionArea>) => void
}) => {
  const methods = area.methodAssignments
  const type = catalogue.degradationTypes.find(
    (item) => item.id === area.degradationTypeId
  )
  const selectedIds = methods.map((method) => method.techniqueId)
  const incompatible = selectedIds.some(
    (id) => !type?.techniqueIds.includes(id)
  )
  const selectMethods = (ids: string[]) =>
    onChange({
      methodAssignments: selectMethodAssignments(methods, ids, catalogue, () =>
        crypto.randomUUID()
      ),
    })
  const updateMethod = (id: string, update: Partial<MethodAssignment>) =>
    onChange({
      methodAssignments: methods.map((method) =>
        method.id === id ? { ...method, ...update } : method
      ),
    })
  return (
    <>
      <Form.Item
        label='Degradation type'
        htmlFor={`${area.id}-degradation`}
        required>
        <Select
          id={`${area.id}-degradation`}
          value={area.degradationTypeId}
          disabled={disabled}
          options={catalogue.degradationTypes.map((item) => ({
            value: item.id,
            label: item.label,
          }))}
          onChange={(id) => onChange({ degradationTypeId: id })}
        />
      </Form.Item>
      <Form.Item
        label='Scanning / NDT techniques'
        htmlFor={`${area.id}-techniques`}
        required>
        <Select
          id={`${area.id}-techniques`}
          mode='multiple'
          value={selectedIds}
          disabled={disabled || !type}
          options={catalogue.techniques
            .filter(
              (item) =>
                type?.techniqueIds.includes(item.id) ||
                selectedIds.includes(item.id)
            )
            .map((item) => ({
              value: item.id,
              label: item.label,
            }))}
          onChange={selectMethods}
        />
      </Form.Item>
      {incompatible && (
        <Alert
          type='warning'
          title='Review selected methods for this degradation type. Their saved details have been retained.'
        />
      )}
      <Space orientation='vertical' size='middle'>
        {methods.map((method) => (
          <MethodAssignmentCard
            key={method.id}
            method={resolveMethodAssignment(method, catalogue)}
            technique={catalogue.techniques.find(
              (item) => item.id === method.techniqueId
            )}
            config={config}
            measurementSystem={measurementSystem}
            countLabel={catalogue.units.count}
            sourceRequiredArea={
              area.scope === 'zone' ? sourceRequiredArea : undefined
            }
            disabled={disabled}
            onChange={(update) => updateMethod(method.id, update)}
          />
        ))}
        <Typography.Paragraph type='secondary'>
          Each method has its own estimate and procedure. Actual scope is
          recorded with inspection results; drawing size does not measure
          physical scope.
        </Typography.Paragraph>
      </Space>
    </>
  )
}
